// ─── Copy Trade Persistence ─────────────────────────────────
// Persists the copy trade pairs + running flag to Supabase so the
// engine can auto-restore across devices (not just same-browser).
// Uses bot_session_state under a fixed strategy_id key, mirroring
// the pattern used by trailing-stop and chin-bot session restore.

import { supabase } from '@/lib/supabase';

export interface PersistedCopyPair {
  sourceAccountId: string;
  sourceAccountName: string;
  targetMt4AccountId: string;
  targetMt4AccountName: string;
  strategyId?: string;
  strategyName?: string;
  enabled?: boolean;
  volumeMode?: 'same' | 'percent' | 'fixed';
  volumePercent?: number;
  fixedVolume?: number;
  label?: string;
}

export interface PersistedCopyTradeConfig {
  pairs: PersistedCopyPair[];
  wasRunning: boolean;
  updatedAt: number;
}

const SESSION_KEY = 'copy_trade_global';

// Same-browser write-time metadata. The panel writes the pair config to
// localStorage SYNCHRONOUSLY on every edit, while the Supabase copy is written
// asynchronously. This timestamp lets restore pick the freshest of the two so a
// just-made volume edit (still in flight to Supabase) is never discarded.
export const COPY_TRADE_LS_META_KEY = 'copy_trade_pairs_meta_v1';

export function markCopyTradeConfigUpdatedLocally(): void {
  try {
    localStorage.setItem(COPY_TRADE_LS_META_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

export function getCopyTradeConfigUpdatedLocally(): number {
  try {
    const v = localStorage.getItem(COPY_TRADE_LS_META_KEY);
    return v ? Number(v) || 0 : 0;
  } catch {
    return 0;
  }
}

/**
 * Save copy trade pairs + running flag to Supabase (user-scoped).
 * Best-effort: failures are logged but never thrown.
 */
export async function saveCopyTradeConfig(
  pairs: PersistedCopyPair[],
  wasRunning: boolean,
): Promise<void> {
  try {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;

    await supabase
      .from('bot_session_state')
      .upsert(
        {
          user_id: data.user.id,
          strategy_id: SESSION_KEY,
          session_data: {
            type: 'copy_trade',
            pairs,
            wasRunning,
            updatedAt: Date.now(),
          } as unknown as Record<string, unknown>,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,strategy_id' },
      );
  } catch (err) {
    console.warn('[CopyTradePersistence] save failed:', err);
  }
}

/**
 * Load copy trade pairs + running flag from Supabase.
 * Returns null when nothing is persisted (or on error).
 */
export async function loadCopyTradeConfig(): Promise<PersistedCopyTradeConfig | null> {
  try {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return null;

    const { data: row } = await supabase
      .from('bot_session_state')
      .select('session_data')
      .eq('user_id', data.user.id)
      .eq('strategy_id', SESSION_KEY)
      .maybeSingle();

    if (!row?.session_data) return null;

    const raw = row.session_data as Record<string, unknown>;
    if (raw.type !== 'copy_trade') return null;

    return {
      pairs: (raw.pairs as PersistedCopyPair[]) || [],
      wasRunning: !!raw.wasRunning,
      updatedAt: (raw.updatedAt as number) || 0,
    };
  } catch {
    return null;
  }
}